import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface GSClientMetrics {
  totalProducts: number;
  saferIngredientsPercentage: number;
  carbonReduced: number;
  waterSaved: number;
  loading: boolean;
  error: string | null;
}

export const useGSClientMetrics = () => {
  const [metrics, setMetrics] = useState<GSClientMetrics>({
    totalProducts: 0,
    saferIngredientsPercentage: 0,
    carbonReduced: 0,
    waterSaved: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setMetrics(prev => ({ ...prev, loading: true, error: null }));

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('User not authenticated');
        }

        // Fetch user's portfolios and products
        const { data: portfolios, error: portfoliosError } = await supabase
          .from('portfolios')
          .select(`
            id,
            title,
            products (
              id,
              name,
              product_type,
              certification_status,
              product_chemicals (
                id,
                chemical_id,
                percentage_typical,
                chemicals (
                  id,
                  chemical_name,
                  chemical_endpoints (
                    risk_level
                  )
                )
              )
            )
          `)
          .eq('user_id', user.id);

        if (portfoliosError) throw portfoliosError;

        // Calculate metrics
        let totalProducts = 0;
        let totalChemicals = 0;
        let saferChemicals = 0;

        portfolios?.forEach(portfolio => {
          if (portfolio.products) {
            totalProducts += portfolio.products.length;
            
            portfolio.products.forEach(product => {
              if (product.product_chemicals) {
                product.product_chemicals.forEach(productChemical => {
                  if (productChemical.chemicals) {
                    totalChemicals++;
                    
                    // Count as safer if majority of endpoints are low or moderate risk
                    const endpoints = productChemical.chemicals.chemical_endpoints || [];
                    const lowRiskCount = endpoints.filter(ep => ep.risk_level === 'low').length;
                    const moderateRiskCount = endpoints.filter(ep => ep.risk_level === 'moderate').length;
                    const totalEndpoints = endpoints.length;
                    
                    if (totalEndpoints > 0) {
                      const saferEndpoints = lowRiskCount + moderateRiskCount;
                      if (saferEndpoints / totalEndpoints >= 0.6) {
                        saferChemicals++;
                      }
                    }
                  }
                });
              }
            });
          }
        });

        const saferIngredientsPercentage = totalChemicals > 0 
          ? Math.round((saferChemicals / totalChemicals) * 100) 
          : 0;

        // For now, use estimated values for carbon and water based on product count
        // These would ideally come from actual sustainability calculations
        const carbonReduced = totalProducts * 12.5; // Estimated kg CO2 per product
        const waterSaved = totalProducts * 150; // Estimated liters per product

        setMetrics({
          totalProducts,
          saferIngredientsPercentage,
          carbonReduced,
          waterSaved,
          loading: false,
          error: null,
        });

      } catch (error) {
        console.error('Error fetching GS Client metrics:', error);
        setMetrics(prev => ({
          ...prev,
          loading: false,
          error: error instanceof Error ? error.message : 'Failed to fetch metrics',
        }));
      }
    };

    fetchMetrics();
  }, []);

  return metrics;
};